import type { FormInstance } from "rc-field-form";
import type { Rule, RuleObject } from "rc-field-form/es/interface";
import * as React from "react";
import { IExtraConnectionStringParam } from "../../../services/connection-string-builder-service";
import { KuikaFormField } from "../../ui-kit/kuika-form-page/kuika-form-page";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import {
  editGridNavId,
  focusEditGridCell,
  IKuikaEditGridColumn,
  KuikaEditGrid,
  KuikaEditGridCell,
  KuikaEditGridRemove,
  KuikaEditGridRow
} from "../shared/kuika-edit-grid/kuika-edit-grid";
import {
  IEditGridListApi,
  IEditGridListRow,
  KuikaEditGridFormList
} from "../shared/kuika-edit-grid/kuika-edit-grid-form-list";
import { blankExtraParam, isBlankExtraParam } from "./sql-connection-engines";

/** Where the grid's rows live in the connection form's store. */
export const EXTRA_PARAMS_PATH: string[] = ["parsedConnectionString", "extraParams"];

export interface IConnectionParametersGridProps {
  /** Prefixes every test id, e.g. `sap` → `sapParameterKeyText`, `sapParameterDraftKeyInput`. */
  testIdPrefix: string;
  /** Why the key at `index` is refused, or null. Never called to refuse a blank row. */
  keyProblem: (params: IExtraConnectionStringParam[] | undefined, index: number) => string | null;
}

/**
 * A connection string's extra parameters: Key and Value, typed straight into the grid. The last
 * row is always the blank one the next parameter is written in — there is no ADD button — and
 * Enter walks key → value → the next row's key, as on Session Variables.
 */
export const ConnectionParametersGrid = ({ testIdPrefix, keyProblem }: IConnectionParametersGridProps): JSX.Element => {
  const columns: IKuikaEditGridColumn[] = [
    // Key is the wider track: a parameter name is longer than most of its values.
    { id: "key", header: "Key", track: "minmax(0, 1.2fr)", headerTestId: `${testIdPrefix}ParameterKeyText` },
    { id: "value", header: "Value", track: "minmax(0, 1fr)", headerTestId: `${testIdPrefix}ParameterValueText` },
    { id: "remove", track: "52px" }
  ];

  const keyRule = (index: number): Rule => {
    const rule = (form: FormInstance): RuleObject => ({
      validator: async (): Promise<void> => {
        const problem: string | null = keyProblem(form.getFieldValue(EXTRA_PARAMS_PATH), index);
        if (problem) throw new Error(problem);
      }
    });
    return rule;
  };

  return (
    <KuikaEditGridFormList<IExtraConnectionStringParam>
      name={EXTRA_PARAMS_PATH}
      isBlank={isBlankExtraParam}
      makeBlank={blankExtraParam}
    >
      {(rows: IEditGridListRow[], { settle, remove }: IEditGridListApi): React.ReactNode => (
        <KuikaEditGrid columns={columns} label="Parameters" testId={`${testIdPrefix}ParametersGrid`}>
          {rows.map((row: IEditGridListRow): JSX.Element => {
            const next: IEditGridListRow | undefined = rows[row.index + 1];
            return (
              <KuikaEditGridRow key={row.key} testId={row.isDraft ? `${testIdPrefix}ParameterDraftRow` : undefined}>
                <KuikaEditGridCell flush>
                  <KuikaFormField name={[row.name, "key"]} rules={[keyRule(row.index)]}>
                    <KuikaInput
                      size="md"
                      borderless
                      placeholder="Enter key.."
                      autoComplete="off"
                      data-testid={row.isDraft ? `${testIdPrefix}ParameterDraftKeyInput` : undefined}
                      data-edit-grid-nav={editGridNavId(row.key, "key")}
                      onChange={settle}
                      onPressEnter={(event: React.KeyboardEvent<HTMLInputElement>): void => {
                        event.preventDefault();
                        focusEditGridCell(event.currentTarget, editGridNavId(row.key, "value"));
                      }}
                    />
                  </KuikaFormField>
                </KuikaEditGridCell>
                <KuikaEditGridCell flush>
                  <KuikaFormField name={[row.name, "value"]}>
                    <KuikaInput
                      size="md"
                      borderless
                      placeholder="Enter value.."
                      autoComplete="off"
                      data-testid={row.isDraft ? `${testIdPrefix}ParameterDraftValueInput` : undefined}
                      data-edit-grid-nav={editGridNavId(row.key, "value")}
                      onChange={settle}
                      onPressEnter={(event: React.KeyboardEvent<HTMLInputElement>): void => {
                        event.preventDefault();
                        if (next) focusEditGridCell(event.currentTarget, editGridNavId(next.key, "key"));
                      }}
                    />
                  </KuikaFormField>
                </KuikaEditGridCell>
                <KuikaEditGridCell center>
                  {/* The draft is where the next parameter goes: there is nothing there to remove. */}
                  {!row.isDraft && (
                    <KuikaEditGridRemove label="Remove parameter" onClick={(): void => remove(row.index)} />
                  )}
                </KuikaEditGridCell>
              </KuikaEditGridRow>
            );
          })}
        </KuikaEditGrid>
      )}
    </KuikaEditGridFormList>
  );
};
